import type { WallPlaybackState } from './wallMediaLabel';

/**
 * WALL feed runtime-health registry (#269 — PART X Ruling 4 tile curation).
 *
 * The #256 classification axes say what a camera SHOULD be (live stream /
 * snapshot / clip), but a catalog-live YouTube channel that isn't currently
 * broadcasting only reveals itself at RUNTIME: the mounted player reports
 * offline / no-picture (#232 runtime honesty). Wall-QA round 1 seated two such
 * feeds — honest OFFLINE placards, but a 50% dead wall while live candidates
 * existed.
 *
 * This registry closes the loop: wall tiles report their player's actual
 * playback state here; the NEXT seating pass (`selectWallFeeds`) demotes
 * recently-dead feeds below every live candidate, so the reel rotation
 * replaces a died tile instead of re-seating it (Ruling 4's mid-dwell death
 * allowance — ≤1 non-live tile while live candidates exist, transiently,
 * until the next rotation).
 *
 * Module-level singleton (the wall is one window; banks share what any tile
 * learned). Pure helpers take an injected `nowMs` so expiry is unit-testable.
 */

/** How long a dead report demotes a feed before it may be retried. A YouTube
 *  channel that resumes broadcasting re-seats after this window (and proves
 *  itself by reporting 'playing', which clears the mark immediately). */
export const WALL_FEED_DEAD_TTL_MS = 15 * 60_000;

interface FeedHealthEntry {
  state: WallPlaybackState;
  atMs: number;
}

const registry = new Map<string, FeedHealthEntry>();

/** Record a mounted wall tile's actual playback state for its feed. */
export function reportWallFeedPlayback(
  feedId: string,
  state: WallPlaybackState,
  nowMs: number = Date.now(),
): void {
  registry.set(feedId, { state, atMs: nowMs });
}

/** True when the feed's most recent runtime report says it is NOT showing
 *  pictures (offline / stalled-over-black) and the report is still fresh.
 *  'loading' is interim, not dead; 'playing' clears the demotion. */
export function isWallFeedDead(feedId: string, nowMs: number = Date.now()): boolean {
  const entry = registry.get(feedId);
  if (!entry) return false;
  if (entry.state !== 'offline' && entry.state !== 'no_video') return false;
  return nowMs - entry.atMs <= WALL_FEED_DEAD_TTL_MS;
}

/** The set of feed ids currently demoted by a fresh dead report — the
 *  `deadIds` input to `selectWallFeeds`. */
export function wallDeadFeedIds(nowMs: number = Date.now()): Set<string> {
  const dead = new Set<string>();
  for (const [id] of registry) {
    if (isWallFeedDead(id, nowMs)) dead.add(id);
  }
  return dead;
}

/** Test/teardown helper — empty the registry. */
export function clearWallFeedHealth(): void {
  registry.clear();
}
